import React from "react"
import {compose} from "redux"
import {withRouter, Switch, Redirect, Route, RouteComponentProps} from "react-router-dom"
// Styles
import "./App.css"

// Constants
import {AppRoute} from "./modules/routing/constants"

// HOCs
import withUser, {WithUserProps} from "./modules/auth/hoc/withUser"
import withAuthorization, {WithAuthorizationProps} from "./modules/auth/hoc/withAuthorization"

// Hooks
import usePrevious from "./hooks/usePrevious"

// Components
import LayoutSwitch from "./components/LayoutSwitch"
import SignInPageSwitch from "./modules/auth/components/SignInPageSwitch"
import HomePageSwitch from "./modules/doctor-stats/components/HomePageSwitch"
import PrivateRoute from "./modules/routing/components/PrivateRoute"

// Containers
import RecordPageContainer from "./modules/records/containers/RecordPageContainer"
import RecordPreviewPageContainer from "./modules/records/containers/RecordPreviewPageContainer"

type AppProps = WithUserProps & WithAuthorizationProps & RouteComponentProps

/**
 * @param props
 */
function App(props: AppProps) {
    const {
        user,
        authorizing,
        fetchCurrentUser,
        history,
        location: {
            state,
            pathname,
        },
    } = props;
    const prevUser = usePrevious(user);

    React.useEffect(() => {
        fetchCurrentUser();
        // eslint-disable-next-line
    }, []);

    React.useEffect(() => {
        if (wasAuthorized()) {
            if (locationStateAvailable()) {
                history.push(state.from.pathname);

                return
            }

            // `|| myInitObject.data == "true"` used to be part of this test.
            // That flag was a mutable module property listed as an effect
            // dependency, which React cannot observe — so this branch fired on
            // an unrelated re-render, at a moment nothing on screen explained.
            // Being unassigned from a record is not a reason to move a doctor
            // who has just signed in; the websocket epic handles it now.
            if (pathname === AppRoute.SignIn) {
                history.push(AppRoute.Home);

                return
            }
        }

        if (wasSignedOut()) {
            history.replace(AppRoute.SignIn);
        }
        // eslint-disable-next-line
    }, [user, history]);

    function wasAuthorized() {
        return userDiffers() && user !== null
    }

    function wasSignedOut() {
        return userDiffers() && user === null
    }

    function userDiffers() {
        return typeof prevUser !== "undefined" && prevUser !== user
    }

    function isAuthorized() {
        return authorizing === false && user !== null
    }

    function locationStateAvailable() {
        return typeof state !== "undefined" && state !== null && typeof state.from !== "undefined"
    }

    if (authorizing) {
        // TODO: Global Loading

        return null
    }

    const authorized = isAuthorized();

    return (
        <LayoutSwitch>
            <Switch>
                <Route exact
                       path={AppRoute.SignIn}
                       component={SignInPageSwitch}/>
                <PrivateRoute exact
                              path={AppRoute.Home}
                              component={HomePageSwitch}
                              isAuthorized={authorized}/>
                <PrivateRoute exact
                              path={AppRoute.Record}
                              isAuthorized={authorized}
                              component={RecordPageContainer}/>
                <PrivateRoute exact
                              isAuthorized={authorized}
                              path={AppRoute.RecordPreview}
                              component={RecordPreviewPageContainer}/>
                <Route path="*" render={() => <Redirect to={AppRoute.Home}/>}/>
            </Switch>
        </LayoutSwitch>
    )
}

export default compose(
    withUser,
    withRouter,
    withAuthorization,
)(App)
